/* eslint @typescript-eslint/strict-boolean-expressions: off */

import Link from "next/link";
import styles from "pages/projects/[slug].module.css";
import { Card } from "gui/card/card";
import { Cell } from "gui/grid/cell";
import { Embed } from "gui/embed/embed";
import { DashboardList } from "gui/entries/dashboard-list";
import { EntryDashboard } from "lib/entries/types";
import { Footer } from "gui/footer/footer";
import { GetStaticPaths } from "next";
import { GetStaticProps } from "next";
import { Grid } from "gui/grid/grid";
import { Header } from "shared/Header/Header";
import { Icon } from "gui/icon/icon";
import { NotFoundPage } from "pages/404";
import { Page } from "gui/page/page";
import { PageHead } from "gui/head/head";
import { Project } from "lib/projects/types";
import { ProjectGrid } from "gui/project/project";
import { ProjectLinks } from "gui/project/project";
import { ProjectLogo } from "gui/project/project";
import { ProjectTags } from "gui/project/project";
import { SanityDocumentPage } from "lib/sanity/types";
import { SanitySections } from "gui/sanity/sections";
import { Spacer } from "gui/spacer/spacer";
import { Subtitle } from "gui/subtitle/subtitle";
import { createThemeColors } from "gui/theme/theme";
import { fetchDashboards } from "lib/entries/entries";
import { fetchPage } from "lib/sanity/pages";
import { fetchProject } from "lib/projects/projects";
import { fetchProjectIds } from "lib/projects/projects";
import { projectCardProps } from "gui/project/project";
import { projectLink } from "lib/links/links";
import { useRouter } from "next/router";

interface Props {
  project: Project;
  page?: SanityDocumentPage;
  preview: boolean;
  dashboards: EntryDashboard[];
}

export const ProjectPage: React.FC<Props> = (props) => {
  const { query } = useRouter();

  if (!props.project || !props.page) {
    return <NotFoundPage />;
  }

  // Render a special variant for social media screenshots
  // when this page is used by the /api/screenshot endpoint.
  if (query.ref === "screenshot") {
    return <ProjectScreenshot project={props.project} />;
  }

  return (
    <>
      <PageHead
        title={`${props.project.name} dashboards and analytics`}
        preview={props.preview}
        screenshot={projectLink(props.project.id)}
      />
      <Header />
      <main>
        <Page style={createProjectTheme(props.project)} size="md" triangle2>
          <ProjectHero project={props.project} />
          <ProjectAnalytics project={props.project} />
          <ProjectEmbeds project={props.project} />
          <ProjectDescription project={props.project} />
          <Spacer size="lg" />
        </Page>
        <Page size="md">
          <ProjectRelated project={props.project} />
          <ProjectDashboards dashboards={props.dashboards} />
          <SanitySections page={props.page} />
        </Page>
      </main>
      <Footer />
    </>
  );
};

const ProjectScreenshot: React.FC<{
  project: Project;
}> = (props) => {
  return (
    <div
      className={styles.screenshot}
      style={createProjectTheme(props.project)}
    >
      <ProjectLogo project={props.project} />
      <img src="/assets/logo.svg" alt="Dune" />
    </div>
  );
};

const ProjectHero: React.FC<{
  project: Project;
}> = (props) => {
  return (
    <>
      <Spacer size="lg" />
      <Grid className={styles.hero} revealOnLoad gaps>
        <div className={styles.square}>
          <header>
            <h1 className={styles.title}>
              Community dashboards and analytics for{" "}
              <strong>{props.project.name}</strong>
            </h1>
          </header>
          <div className={styles.meta}>
            <ProjectTags project={props.project} />
            <ProjectLinks project={props.project} />
          </div>
        </div>
        <div className={styles.logo}>
          <ProjectLogo project={props.project} />
        </div>
      </Grid>
    </>
  );
};

const ProjectAnalytics: React.FC<{
  project: Project;
}> = (props) => {
  return (
    <Grid revealOnLoad gaps>
      <Subtitle text="Featured dashboards and queries" icon="bar-chart">
        <Link
          href="/browse/[slug]"
          as={`/browse/dashboards?q=${props.project.id}`}
          prefetch={false}
        >
          <a>
            ...or search for more
            <Icon icon="arrow-right" />
          </a>
        </Link>
      </Subtitle>
      <ProjectAnalyticsHelp project={props.project} />
      {projectCardProps(props.project).map((card, i) => (
        <Card key={i} {...card} />
      ))}
    </Grid>
  );
};

const ProjectAnalyticsHelp: React.FC<{
  project: Project;
}> = (props) => {
  const schemas = props.project.schemas?.length
    ? `Schemas: ${props.project.schemas.join(", ")}`
    : null;

  return (
    <div className={styles.help}>
      <p>
        Check out these dashboards for up-to-date analytics about{" "}
        <strong>{props.project.name}</strong> and other related projects.
      </p>
      <ul className={styles.meta}>
        {schemas && (
          <li>
            <a href="https://github.com/duneanalytics/abstractions">
              {schemas}
            </a>
          </li>
        )}
        <li>
          <a href="https://dune.com/decode">Decode more data</a>
        </li>
        <li>
          <a href="https://github.com/duneanalytics/projects">Edit this page</a>
        </li>
      </ul>
    </div>
  );
};

const ProjectEmbeds: React.FC<{
  project: Project;
}> = (props) => {
  if (!props.project.embeds?.length) {
    return null;
  }

  return (
    <>
      <Spacer />
      {props.project.embeds.map((embed, i) => (
        <Embed
          key={i}
          title={embed.name}
          link={embed.link}
          watermark
          transparent
        />
      ))}
    </>
  );
};

const ProjectDescription: React.FC<{
  project: Project;
}> = (props) => {
  if (!props.project.description) {
    return null;
  }

  return (
    <Grid revealOnScroll gaps>
      <Spacer />
      <Subtitle text={`About ${props.project.name}`} icon="question" />
      <div className={styles.description}>
        <div
          className={styles.html}
          dangerouslySetInnerHTML={{ __html: props.project.description }}
        />
      </div>
    </Grid>
  );
};

const ProjectRelated: React.FC<{
  project: Project;
}> = (props) => {
  if (props.project.related.length === 0) {
    return null;
  }

  return (
    <>
      <Grid revealOnScroll gaps>
        <Subtitle text="Related projects" icon="star" />
      </Grid>
      {props.project.related.map((project) => (
        <ProjectGrid key={project.id} project={project} revealOnScroll />
      ))}
      <Spacer size="lg" />
    </>
  );
};

const ProjectDashboards: React.FC<{
  dashboards: EntryDashboard[];
}> = (props) => {
  if (props.dashboards.length === 0) {
    return null;
  }

  return (
    <Grid gaps>
      <Subtitle text="Dashboards with related tags" icon="tag" />
      <Cell>
        <DashboardList entries={props.dashboards} color2 avatar />
      </Cell>
      <Spacer size="lg" />
    </Grid>
  );
};

// Create CSS theme variables based on the project color,
// or fall back to using the default page theme.
export const createProjectTheme = (project: Project) => {
  return project.color ? createThemeColors(project.color) : undefined;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const ids = await fetchProjectIds();

  const paths = ids.map((id) => {
    return { params: { slug: id } };
  });

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props> = async (ctx) => {
  const slug = ctx.params!.slug as string;
  const preview = Boolean(ctx.preview);
  const project = await fetchProject(slug, true);
  const dashboards = await fetchProjectDashboards(project);

  // Fetch content from the special "project-data" slug.
  const page = await fetchPage("project-data", preview);

  return {
    props: {
      project,
      page,
      preview,
      dashboards,
    },
  };
};

// Fetch a list of dashboards related to this project, based on tags.
export const fetchProjectDashboards = async (
  project: Project
): Promise<EntryDashboard[]> => {
  if (!project.tags?.length) {
    return [];
  }

  const res = await fetchDashboards({
    tags: project.tags,
    page_size: 5,
  });

  return res?.results ?? [];
};

export default ProjectPage;
